import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import BindingComponent from './BindingComponent';
import MultiLevelSelect from 'components/form-components/MultiLevelSelect';
import { useTranslation } from 'react-i18next';

const CompanyFieldBasicProperties = ({
  companyFieldList,
  selectedCompanyFieldIds,
  companyFieldMap,
  companyFieldDisplayValues,
  properties,
  handleChange,
  otherDetails,
}) => {
  const { t } = useTranslation();
  const [columnValueList, setColumValueList] = useState([]);

  const companyFieldOptions = useMemo(() =>
    companyFieldList
      .filter((field) => selectedCompanyFieldIds.includes(field._id))
      .map((field) => ({
        displayValue: field.fieldTitle,
        value: field._id,
      })),
  );

  useEffect(() => {
    const allOptions = companyFieldMap[properties['companyFieldId']]?.fieldOptions || [];
    const selectedCompanyFieldDataSourceColumns = [];
    allOptions.forEach((opt) => {
      selectedCompanyFieldDataSourceColumns.push({
        value: opt.value,
        displayValue: opt.displayValue,
      });
    });
    setColumValueList(selectedCompanyFieldDataSourceColumns);
  }, [properties.companyFieldId]);

  return (
    <div className='d-flex flex-column gap-2'>
      <div>
        <label className='theme-size-0_8'>{t('form_field_company_field')}</label>
        <MultiLevelSelect
          name='companyFieldId'
          options={{ children: companyFieldOptions }}
          value={[]}
          handleChange={(name, value) => {
            handleChange(name, value[0], {
              companyFieldColumn: [companyFieldMap[value[0]].fieldColumn],
            });
          }}
          toggleClasses='px-2 py-2 d-flex gap-2 justify-content-between align-items-center rounded-sm cursor-pointer border-grey-1-h-1'
          toggleComponent={
            <>
              <label className='cursor-pointer'>
                {t(
                  companyFieldList.find((f) => f._id === properties['companyFieldId'])
                    ?.fieldTitle ?? 'select_select',
                )}
              </label>
              <img src={chevronGreyIcon} className='rotate-180' />
            </>
          }
          fullWidth
        />
      </div>

      {(() => {
        const values = properties['companyFieldDisplayColumns'] || [];
        const options = companyFieldMap[properties['companyFieldId']]?.fieldOptions || [];
        if (values.length === 0 || options.length === 0) return null;

        return (
          <div className='d-flex flex-column'>
            {values.map((val) => {
              const opt = options.find((o) => o.value === val);
              return opt ? <span key={val}>{opt.displayValue}</span> : null;
            })}
          </div>
        );
      })()}

      <div>
        <label className='theme-size-0_8'>{t('field_display_columns')}</label>
        <MultiLevelSelect
          name='companyFieldDisplayColumns'
          options={{
            children: companyFieldMap[properties['companyFieldId']]?.fieldOptions || [],
            selectMultiple: true,
          }}
          value={properties['companyFieldDisplayColumns']}
          handleChange={handleChange}
          toggleClasses='px-2 py-2 d-flex gap-2 justify-content-between align-items-center rounded-sm cursor-pointer border-grey-1-h-1'
          toggleComponent={
            <>
              <label className='theme-size-0_9 cursor-pointer '>
                {(() => {
                  const val = properties['companyFieldDisplayColumns'];
                  return (
                    <>
                      {val.length > 0
                        ? companyFieldDisplayValues[val[val.length - 1]]
                        : t('select_select')}
                      {val.length > 1 && ` +${val.length - 1} more`}
                    </>
                  );
                })()}
              </label>
              <img src={chevronGreyIcon} className='rotate-180' />
            </>
          }
          fullWidth
        />
      </div>

      <BindingComponent
        title={'form_bind_fields_to_company_field'}
        properties={properties}
        handleChange={handleChange}
        otherDetails={otherDetails}
        columnValueList={columnValueList}
        bindingKey='bindingList'
      />
    </div>
  );
};

CompanyFieldBasicProperties.propTypes = {
  companyFieldList: PropTypes.array.isRequired,
  selectedCompanyFieldIds: PropTypes.array.isRequired,
  companyFieldMap: PropTypes.object,
  companyFieldDisplayValues: PropTypes.object,
  properties: PropTypes.object.isRequired,
  otherDetails: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
};

export default CompanyFieldBasicProperties;
